import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListMusic,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Plus,
  Check,
  X,
  FileImage,
  Sparkles,
  ChevronLeft,
  RotateCcw,
  RefreshCcw,
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { CANONICAL_BUCKETS } from '@/lib/genres';
import LockedOverlay from '@/components/LockedOverlay';
import useSubscription from '@/hooks/useSubscription';

/**
 * TracklistUpload — parse a SINGLE image containing MANY tracks (DJ setlist,
 * festival lineup, playlist screenshot, liner notes, etc.) and let the user
 * review/edit the extracted rows before committing them to their crates.
 *
 * Flow:
 *   1. "pick"   → drop / choose 1 image
 *   2. "review" → server returns parsed rows; user edits, removes, or adds
 *   3. "done"   → commit → toast → back to Crate
 */
export default function TracklistUpload() {
  const navigate = useNavigate();
  const { isLocked } = useSubscription();
  const [step, setStep] = useState('pick'); // pick | parsing | review | committing | done
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [rows, setRows] = useState([]); // [{position, artist, title, confidence}]
  const [sourceHint, setSourceHint] = useState(null);
  const [notes, setNotes] = useState(null);
  const [sourceLabel, setSourceLabel] = useState('');
  const [targetBucket, setTargetBucket] = useState('__auto__');
  const [isDragging, setIsDragging] = useState(false);
  // Per-row re-parse busy set — used to spin the row's icon while the
  // vision model takes another shot at that specific track. Set of _rid
  // strings; using a Set (via a serialized array in useState) avoids
  // stale-closure gotchas.
  const [rowReparsing, setRowReparsing] = useState([]);
  // Global "re-parse all" busy flag (separate from initial `step==='parsing'`
  // so the loading spinner overlays the current review UI without wiping it).
  const [reparsingAll, setReparsingAll] = useState(false);
  const fileInputRef = useRef(null);

  const rowsWithArtistAndTitle = useMemo(
    () => rows.filter((r) => (r.artist || '').trim() && (r.title || '').trim()),
    [rows],
  );

  if (isLocked) {
    return (
      <div className="max-w-3xl mx-auto py-6">
        <LockedOverlay />
      </div>
    );
  }

  const pickFile = (file) => {
    if (!file || !file.type?.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setRawFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  const startParse = async () => {
    if (!rawFile) return;
    setStep('parsing');
    try {
      const form = new FormData();
      form.append('file', rawFile);
      const { data } = await api.post('/tracklist/parse', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = (data?.tracks || []).map((t, idx) => ({
        // stable client-side row id lets us keep React keys stable while editing
        _rid: `${Date.now()}-${idx}`,
        position: t.position ?? null,
        artist: t.artist || '',
        title: t.title || '',
        confidence: t.confidence ?? 0.75,
      }));
      setRows(parsed);
      setSourceHint(data?.source_hint || null);
      setNotes(data?.notes || null);
      if (parsed.length === 0) {
        toast.error('Couldn\u2019t find any tracks in that image. Try a clearer screenshot.');
        setStep('pick');
        return;
      }
      toast.success(`Found ${parsed.length} ${parsed.length === 1 ? 'track' : 'tracks'} — review below`);
      setStep('review');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tracklist parsing failed');
      setStep('pick');
    }
  };

  const updateRow = (rid, patch) => {
    setRows((prev) => prev.map((r) => (r._rid === rid ? { ...r, ...patch } : r)));
  };

  const removeRow = (rid) => {
    setRows((prev) => prev.filter((r) => r._rid !== rid));
  };

  const addBlankRow = () => {
    setRows((prev) => [
      ...prev,
      { _rid: `${Date.now()}-manual-${prev.length}`, position: null, artist: '', title: '', confidence: 1.0 },
    ]);
  };

  // Re-parse the ENTIRE image from scratch. Confirmation guard so a stray
  // click doesn't wipe the user's edits — per the user's spec.
  const reparseAll = async () => {
    if (!rawFile) {
      toast.error('Original image no longer available. Please re-upload.');
      return;
    }
    if (rows.some((r) => r.artist.trim() || r.title.trim())) {
      const ok = window.confirm(
        'Re-parse the whole tracklist? Your current edits on this page will be replaced.'
      );
      if (!ok) return;
    }
    setReparsingAll(true);
    try {
      const form = new FormData();
      form.append('file', rawFile);
      const { data } = await api.post('/tracklist/parse', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = (data?.tracks || []).map((t, idx) => ({
        _rid: `${Date.now()}-reparse-${idx}`,
        position: t.position ?? null,
        artist: t.artist || '',
        title: t.title || '',
        confidence: t.confidence ?? 0.75,
      }));
      if (parsed.length === 0) {
        toast.error('The re-parse didn\u2019t find any tracks either. Try a sharper image.');
      } else {
        setRows(parsed);
        setSourceHint(data?.source_hint || null);
        setNotes(data?.notes || null);
        toast.success(`Re-parsed \u2014 found ${parsed.length} track${parsed.length === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-parse failed');
    } finally {
      setReparsingAll(false);
    }
  };

  // Re-parse a SINGLE row. Sends the same image plus a hint (position and/or
  // current guess) so the LLM can re-focus on just that track and take a
  // fresh, careful read. Replaces artist/title/confidence on success.
  const reparseRow = async (rid) => {
    if (!rawFile) {
      toast.error('Original image no longer available. Please re-upload.');
      return;
    }
    const row = rows.find((r) => r._rid === rid);
    if (!row) return;
    setRowReparsing((prev) => [...prev, rid]);
    try {
      const form = new FormData();
      form.append('file', rawFile);
      if (row.position != null) form.append('position', String(row.position));
      if (row.artist) form.append('current_artist', row.artist);
      if (row.title) form.append('current_title', row.title);
      const { data } = await api.post('/tracklist/reparse-row', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const t = data?.track;
      if (!t) {
        toast.error(data?.notes || 'Still couldn\u2019t read that track clearly.');
        return;
      }
      updateRow(rid, {
        artist: t.artist || row.artist,
        title: t.title || row.title,
        confidence: t.confidence ?? row.confidence,
      });
      toast.success(`Re-parsed \u2014 ${t.artist} \u2014 ${t.title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-parse failed');
    } finally {
      setRowReparsing((prev) => prev.filter((x) => x !== rid));
    }
  };

  const commit = async () => {
    if (rowsWithArtistAndTitle.length === 0) {
      toast.error('Add at least one complete artist + title row before committing.');
      return;
    }
    setStep('committing');
    try {
      const payload = {
        tracks: rowsWithArtistAndTitle.map(({ artist, title }) => ({ artist, title })),
        target_bucket: targetBucket === '__auto__' ? null : targetBucket,
        source_label: sourceLabel.trim() || sourceHint || null,
      };
      const { data } = await api.post('/tracklist/commit', payload);
      const created = data?.created ?? 0;
      const duplicates = data?.duplicates ?? 0;
      const failed = data?.failed ?? 0;
      const needsReview = data?.needs_review ?? 0;
      const byBucket = data?.by_bucket || {};

      // Build a helpful summary that shows *where* the tracks landed. This
      // is the fix for the reported issue where users didn't understand why
      // some tracks weren't in the expected crate — they'd fallen to
      // "Other" via the classifier fallback.
      const bucketSummary = Object.entries(byBucket)
        .sort((a, b) => b[1] - a[1])
        .map(([bucket, count]) => `${count} in ${bucket}`)
        .join(', ');

      const bits = [
        `${created} added`,
        duplicates ? `${duplicates} already in your crate` : null,
        // Surface "needs review" prominently so users know to double-check.
        // These are cards we couldn't verify against iTunes/MusicBrainz —
        // they're committed with the user's OCR values intact, just flagged.
        needsReview ? `${needsReview} need${needsReview === 1 ? 's' : ''} review` : null,
        failed ? `${failed} couldn\u2019t be enriched` : null,
      ].filter(Boolean).join(' \u00b7 ');

      toast.success(
        bucketSummary
          ? `Tracklist committed \u2014 ${bits}. Landed: ${bucketSummary}.`
          : `Tracklist committed \u2014 ${bits}`,
        { duration: 6000 },
      );
      setStep('done');
      // Small delay so the toast is visible before we navigate.
      setTimeout(() => {
        if (targetBucket && targetBucket !== '__auto__') {
          navigate(`/album/${encodeURIComponent(targetBucket)}`);
        } else {
          navigate('/crate');
        }
      }, 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
      setStep('review');
    }
  };

  const resetAll = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRawFile(null);
    setPreviewUrl(null);
    setRows([]);
    setSourceHint(null);
    setNotes(null);
    setSourceLabel('');
    setTargetBucket('__auto__');
    setStep('pick');
  };

  return (
    <div className="max-w-3xl mx-auto pb-24">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/upload')}
          aria-label="Back"
          data-testid="tracklist-back"
          className="rounded-lg"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <ListMusic className="w-6 h-6 sm:w-7 sm:h-7 text-[hsl(var(--primary))]" />
            Parse tracklist
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            One image, many tracks. Great for DJ setlists, festival lineups, and playlist screenshots.
          </p>
        </div>
      </div>

      {/* ---------------- STEP 1: pick ---------------- */}
      {step === 'pick' && (
        <div
          data-testid="tracklist-dropzone"
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          className={`mt-3 rounded-2xl border-2 border-dashed p-6 sm:p-10 text-center transition-colors duration-200 ${
            isDragging ? 'border-[hsl(var(--primary))] bg-white/5' : 'border-border bg-card'
          }`}
        >
          <div className="mx-auto w-12 h-12 rounded-full bg-secondary grid place-items-center mb-3">
            <ImageIcon className="w-6 h-6 text-[hsl(var(--primary))]" />
          </div>
          <h3 className="font-display text-lg font-semibold">Drop a tracklist screenshot</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Up to 30 tracks per image. Works with 1001Tracklists, rekordbox history, festival posters, playlist screenshots, and more.
          </p>
          <div className="mt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="tracklist-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickFile(f);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="tracklist-file-picker-button"
              className="rounded-xl"
            >
              <FileImage className="w-4 h-4 mr-2" /> Choose image
            </Button>
          </div>

          {previewUrl && (
            <div className="mt-6 grid place-items-center gap-3">
              <img
                src={previewUrl}
                alt="Tracklist preview"
                className="max-h-64 rounded-xl border border-border shadow"
                data-testid="tracklist-preview-image"
              />
              <div className="flex gap-2">
                <Button
                  onClick={startParse}
                  disabled={!rawFile}
                  className="rounded-xl"
                  data-testid="tracklist-parse-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Parse this image
                </Button>
                <Button variant="ghost" onClick={resetAll} className="rounded-xl">
                  <X className="w-4 h-4 mr-2" /> Clear
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------------- STEP 2: parsing (loading spinner) ---------------- */}
      {step === 'parsing' && (
        <div
          className="mt-3 rounded-2xl border border-border bg-card p-10 grid place-items-center gap-4 text-center"
          data-testid="tracklist-parsing"
        >
          <Loader2 className="w-8 h-8 text-[hsl(var(--primary))] animate-spin" />
          <div>
            <p className="font-display font-semibold">Reading your tracklist…</p>
            <p className="text-xs text-muted-foreground mt-1">
              GPT-4o Vision is looking for artists + titles. Usually 5–15 seconds.
            </p>
          </div>
        </div>
      )}

      {/* ---------------- STEP 3: review ---------------- */}
      {step === 'review' && (
        <div className="mt-3 space-y-4">
          <div className="rounded-2xl border border-border bg-card p-3 sm:p-4 flex items-start gap-3">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Source"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border border-border shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <Badge variant="secondary" className="text-[10px]">
                  {rowsWithArtistAndTitle.length} track{rowsWithArtistAndTitle.length === 1 ? '' : 's'}
                </Badge>
                {sourceHint && (
                  <Badge variant="outline" className="text-[10px]" data-testid="tracklist-source-hint">
                    {sourceHint}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Fix anything the AI got wrong, then commit. Rows with a low confidence badge deserve a second look. Tap the arrow icon on any row to have the AI take another shot at just that track.
              </p>
              {notes && (
                <p className="text-[11px] text-muted-foreground/80 mt-1 italic">
                  Note: {notes}
                </p>
              )}
            </div>
            {/* Global "Re-parse all" — re-sends the same image so the AI
                takes another pass at the whole list. Useful when most rows
                are wrong (e.g. rotated / low-contrast source). */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reparseAll}
              disabled={reparsingAll || rowReparsing.length > 0}
              className="rounded-lg shrink-0 h-8 text-xs"
              data-testid="tracklist-reparse-all"
              title="Re-parse the entire image"
            >
              {reparsingAll ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
              )}
              <span className="hidden sm:inline">Re-parse all</span>
              <span className="sm:hidden">Re-parse</span>
            </Button>
          </div>

          {/* Target crate + label */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tracklist-target-bucket" className="text-xs">
                Route to crate
              </Label>
              <Select value={targetBucket} onValueChange={setTargetBucket}>
                <SelectTrigger
                  id="tracklist-target-bucket"
                  className="rounded-lg mt-1"
                  data-testid="tracklist-target-bucket"
                >
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto-detect per track</SelectItem>
                  {CANONICAL_BUCKETS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Force every track into one crate, or let GroovLens classify each row.
              </p>
            </div>
            <div>
              <Label htmlFor="tracklist-source-label" className="text-xs">
                Source label (optional)
              </Label>
              <Input
                id="tracklist-source-label"
                value={sourceLabel}
                onChange={(e) => setSourceLabel(e.target.value)}
                placeholder={sourceHint || 'e.g. Boiler Room 128 · Peggy Gou'}
                className="rounded-lg mt-1"
                data-testid="tracklist-source-label"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Stored on each imported card so you can find them later.
              </p>
            </div>
          </div>

          {/* Editable rows */}
          <ul className="space-y-2" data-testid="tracklist-rows">
            {rows.map((r, idx) => (
              <li
                key={r._rid}
                className="rounded-xl border border-border bg-card p-2.5 sm:p-3 flex items-start gap-2"
                data-testid="tracklist-row"
              >
                <div className="w-7 h-7 shrink-0 rounded-md bg-secondary grid place-items-center text-[11px] font-mono text-muted-foreground">
                  {r.position ?? idx + 1}
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    value={r.artist}
                    onChange={(e) => updateRow(r._rid, { artist: e.target.value })}
                    placeholder="Artist"
                    className="h-9 rounded-lg text-sm"
                    data-testid="tracklist-row-artist"
                    aria-label={`Artist for row ${idx + 1}`}
                  />
                  <Input
                    value={r.title}
                    onChange={(e) => updateRow(r._rid, { title: e.target.value })}
                    placeholder="Title"
                    className="h-9 rounded-lg text-sm"
                    data-testid="tracklist-row-title"
                    aria-label={`Title for row ${idx + 1}`}
                  />
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.confidence < 0.7 && (
                    <Badge
                      variant="outline"
                      className="text-[9px] uppercase border-[hsl(36_100%_60%)]/50 text-[hsl(36_100%_60%)]"
                      title={`Confidence: ${Math.round((r.confidence || 0) * 100)}%`}
                      data-testid="tracklist-row-low-confidence"
                    >
                      Low
                    </Badge>
                  )}
                  {/* Per-row re-parse — asks the AI to take another careful
                      look at just this track. Uses position (if visible) or
                      current guess as a hint. */}
                  <button
                    type="button"
                    onClick={() => reparseRow(r._rid)}
                    disabled={rowReparsing.includes(r._rid) || reparsingAll}
                    aria-label="Re-parse this track"
                    title="Re-parse this track"
                    className="w-7 h-7 rounded-md grid place-items-center text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="tracklist-row-reparse"
                  >
                    {rowReparsing.includes(r._rid) ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(r._rid)}
                    aria-label="Remove row"
                    className="w-7 h-7 rounded-md grid place-items-center text-muted-foreground hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 transition-colors duration-150"
                    data-testid="tracklist-row-remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={addBlankRow}
              className="rounded-lg"
              data-testid="tracklist-add-row"
            >
              <Plus className="w-4 h-4 mr-2" /> Add row
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" onClick={resetAll} className="rounded-lg">
              <X className="w-4 h-4 mr-2" /> Discard
            </Button>
            <Button
              onClick={commit}
              disabled={rowsWithArtistAndTitle.length === 0}
              className="rounded-lg"
              data-testid="tracklist-commit-button"
            >
              <Check className="w-4 h-4 mr-2" />
              Commit {rowsWithArtistAndTitle.length} track{rowsWithArtistAndTitle.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- STEP 4: committing / done ---------------- */}
      {(step === 'committing' || step === 'done') && (
        <div
          className="mt-3 rounded-2xl border border-border bg-card p-10 grid place-items-center gap-3 text-center"
          data-testid="tracklist-committing"
        >
          {step === 'committing' ? (
            <>
              <Loader2 className="w-8 h-8 text-[hsl(var(--primary))] animate-spin" />
              <p className="text-sm text-muted-foreground">Enriching + filing tracks…</p>
            </>
          ) : (
            <>
              <Check className="w-8 h-8 text-[var(--brand-lime)]" />
              <p className="text-sm text-muted-foreground">Tracklist committed. Heading back to your crates.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
